import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useRedirection } from './useRedirection';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { selectProfileDone } from '../store/reducers/profileSlice';
import { STORE_NAME } from '@intigral/constant';
import { ROUTE_CONSTANT } from '@libs/constants';
import type { IProfileData, IProfileList } from '@intigral/types';
import h from '../helpers/common';
import url from '../helpers/url';
import user from '../helpers/user';
import { isGuestUser } from '../config/guestMode';
import { setGuestKidsMode } from '../guest/session';

export function isParentalPinRequired(): boolean {
    const hasPIN = (h.getStore(STORE_NAME.USER_PROFILE_LIST) as IProfileList)?.parentalLockOn || false;
    const profileIsLocked = (h.getStore(STORE_NAME.SELECTED_PROFILE) as IProfileData)?.parentalLockOn || false;
    return hasPIN && profileIsLocked;
}

export function useProfileExit() {
    const redirectTo = useRedirection();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    const clearSelectedProfile = useCallback(() => {
        dispatch(selectProfileDone({}));
        h.deleteStore(STORE_NAME.SELECTED_PROFILE);
        h.setContext({ [STORE_NAME.SELECTED_PROFILE]: {} });
        void queryClient.invalidateQueries({ queryKey: ['profiles'] });
    }, [dispatch, queryClient]);

    const forceProfileExit = useCallback(() => {
        redirectTo(ROUTE_CONSTANT.SELECT_PROFILE);
        clearSelectedProfile();
    }, [clearSelectedProfile, redirectTo]);

    const exitKidsMode = useCallback(
        async (isPinVerified = false) => {
            if (isGuestUser(user.getUserInfo())) {
                setGuestKidsMode(false);
                navigate(url.route(''), { replace: true });
                return;
            }

            const isPinRequired = isParentalPinRequired();

            if (!isPinRequired || isPinVerified) {
                return forceProfileExit();
            }
            if (!h.isParentalPinScreen()) {
                redirectTo(`${ROUTE_CONSTANT.PARENTAL_PIN_MANAGEMENT}?type=${window.btoa('kid-exit|validate')}`);
            }
        },
        [forceProfileExit, navigate, redirectTo]
    );

    return {
        clearSelectedProfile,
        forceProfileExit,
        exitKidsMode,
    };
}
